import React, { useState } from 'react';
import {
    Typography,
    Box,
    Card,
    CardContent,
    CardActions,
    Button,
    Grid,
    Chip,
    IconButton,
    TextField,
    CircularProgress,
    Alert,
    Snackbar,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    LinearProgress
} from '@mui/material';
import {
    Lightbulb as SuggestionIcon,
    Check as ApplyIcon,
    Refresh as RefreshIcon,
    Info as InfoIcon
} from '@mui/icons-material';
import { useQuery, useMutation } from 'react-query';
import { api } from '../utils/api';

function PatternSuggestionPanel({ url, onApplySuggestion }) {
    const [suggestions, setSuggestions] = useState([]);
    const [analysisResult, setAnalysisResult] = useState(null);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [confidenceThreshold, setConfidenceThreshold] = useState(0.7);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success'
    });

    // Fetch common patterns
    const { data: patternsData, isLoading: isPatternsLoading } = useQuery(
        'patterns',
        () => api.get('/api/patterns').then(res => res.data)
    );

    // Analyze page for pattern suggestions
    const analyzePatterns = useMutation((htmlContent) =>
        api.post('/api/patterns/recognize', { html_content: htmlContent }), {
        onMutate: () => {
            setIsAnalyzing(true);
        },
        onSuccess: (response) => {
            setAnalysisResult(response.data);
            if (response.data.selectors) {
                // Convert selectors to suggestion format
                const newSuggestions = Object.entries(response.data.selectors).map(([key, value]) => {
                    // Ensure selector value is properly handled (could be string or object)
                    const selectorValue = typeof value === 'object' && value !== null && value.selector
                        ? value.selector
                        : value;
                    return {
                        element: key,
                        selector: selectorValue,
                        confidence: response.data.confidence_scores?.[key] || 0,
                        source: 'automated'
                    };
                });
                setSuggestions(newSuggestions);
            }
        },
        onError: (error) => {
            showSnackbar('Pattern analysis failed', 'error');
        },
        onSettled: () => {
            setIsAnalyzing(false);
        }
    });

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({
            open: true,
            message,
            severity
        });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    // Handle analyze button click
    const handleAnalyze = () => {
        // In a real implementation, we would fetch the HTML content of the page
        // For now, we'll use a mock implementation
        analyzePatterns.mutate('<html><body>Mock HTML content for analysis</body></html>');
    };

    // Handle apply suggestion
    const handleApplySuggestion = (suggestion) => {
        // Extract selector value properly if it's an object
        const selectorValue = typeof suggestion.selector === 'object' && suggestion.selector !== null && suggestion.selector.selector
            ? suggestion.selector.selector
            : suggestion.selector;
        if (onApplySuggestion) {
            onApplySuggestion(suggestion.element, selectorValue);
            showSnackbar(`Applied suggestion for ${suggestion.element}`);
        }
    };

    // Filter suggestions by confidence threshold
    const filteredSuggestions = suggestions.filter(
        suggestion => suggestion.confidence >= confidenceThreshold
    );

    // Get common patterns for an element type
    const getCommonPatterns = (element) => {
        if (!patternsData?.patterns) return [];

        // Find pattern that matches the element name
        const pattern = patternsData.patterns.find(p =>
            element.toLowerCase().includes(p.name.toLowerCase())
        );

        return pattern ? pattern.selectors : [];
    };

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                    Pattern Suggestions
                </Typography>
                <Button
                    variant="contained"
                    startIcon={isAnalyzing ? <CircularProgress size={20} /> : <RefreshIcon />}
                    onClick={handleAnalyze}
                    disabled={isAnalyzing}
                >
                    Analyze Page
                </Button>
            </Box>

            {isAnalyzing && (
                <Box sx={{ mb: 2 }}>
                    <LinearProgress />
                    <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                        Analyzing page structure for pattern suggestions...
                    </Typography>
                </Box>
            )}

            {analysisResult && !analysisResult.success && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {analysisResult.message}
                </Alert>
            )}

            <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Confidence Threshold
                </Typography>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={9}>
                        <LinearProgress
                            variant="determinate"
                            value={confidenceThreshold * 100}
                            sx={{ mt: 1 }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            type="number"
                            value={confidenceThreshold}
                            onChange={(e) => setConfidenceThreshold(parseFloat(e.target.value) || 0)}
                            inputProps={{ min: 0, max: 1, step: 0.1 }}
                            size="small"
                            fullWidth
                        />
                    </Grid>
                </Grid>
                <Typography variant="body2" color="textSecondary">
                    Show suggestions with confidence score above {confidenceThreshold * 100}%
                </Typography>
            </Box>

            {isPatternsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {filteredSuggestions.map((suggestion, index) => (
                        <Grid item xs={12} key={index}>
                            <Card>
                                <CardContent>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                        <Box>
                                            <Typography variant="h6" component="div">
                                                {suggestion.element.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                <Chip
                                                    label={`${Math.round(suggestion.confidence * 100)}% confidence`}
                                                    color={suggestion.confidence > 0.8 ? 'success' : suggestion.confidence > 0.6 ? 'warning' : 'error'}
                                                    size="small"
                                                    sx={{ mr: 1 }}
                                                />
                                                <Chip
                                                    label={suggestion.source === 'automated' ? 'Automated' : 'Common Pattern'}
                                                    variant="outlined"
                                                    size="small"
                                                />
                                            </Box>
                                        </Box>
                                        <IconButton
                                            onClick={() => handleApplySuggestion(suggestion)}
                                            color="primary"
                                            title="Apply this suggestion"
                                        >
                                            <ApplyIcon />
                                        </IconButton>
                                    </Box>

                                    <TextField
                                        fullWidth
                                        value={typeof suggestion.selector === 'object' && suggestion.selector !== null && suggestion.selector.selector
                                            ? suggestion.selector.selector
                                            : suggestion.selector}
                                        variant="outlined"
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                        sx={{ mb: 2 }}
                                    />

                                    {suggestion.source === 'automated' && (
                                        <Box>
                                            <Typography variant="subtitle2" gutterBottom>
                                                Common patterns for this element:
                                            </Typography>
                                            <List dense>
                                                {getCommonPatterns(suggestion.element).map((pattern, idx) => (
                                                    <ListItem key={idx} sx={{ py: 0.5 }}>
                                                        <ListItemIcon sx={{ minWidth: 30 }}>
                                                            <InfoIcon fontSize="small" color="action" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={pattern}
                                                            secondary={
                                                                <Button
                                                                    size="small"
                                                                    onClick={() => {
                                                                        const newSuggestion = {
                                                                            ...suggestion,
                                                                            selector: pattern
                                                                        };
                                                                        handleApplySuggestion(newSuggestion);
                                                                    }}
                                                                >
                                                                    Use this pattern
                                                                </Button>
                                                            }
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    )}
                                </CardContent>
                                <CardActions>
                                    <Button
                                        size="small"
                                        startIcon={<ApplyIcon />}
                                        onClick={() => handleApplySuggestion(suggestion)}
                                    >
                                        Apply Suggestion
                                    </Button>
                                </CardActions>
                            </Card>
                        </Grid>
                    ))}

                    {filteredSuggestions.length === 0 && !isAnalyzing && (
                        <Grid item xs={12}>
                            <Box sx={{ textAlign: 'center', p: 4 }}>
                                <SuggestionIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">
                                    No pattern suggestions available
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                    Click "Analyze Page" to get suggestions for CSS selectors
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            )}

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default PatternSuggestionPanel;